import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTrash, faSpinner, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { Site, KustomIntegrationSettings as KustomIntegrationSettingsType } from '@/types';

const KustomIntegration: React.FC = () => {
  const [formData, setFormData] = useState({
    site_id: '',
    payouts_read_latest_poll_time: '',
  });

  const [alerts, setAlerts] = useState({
    saveSuccess: false,
    deleteSuccess: false,
    error: false,
  });

  const queryClient = useQueryClient();

  // Fetch sites
  const { data: sites = [] } = useQuery('sites', apiService.getSites);
  const siteId = sites.length > 0 ? sites[0].id : null;

  // Fetch Kustom integration settings
  const { data: settings, isLoading } = useQuery(
    ['kustomIntegrationSettings', siteId],
    () => apiService.getKustomIntegrationSettings(siteId!),
    {
      enabled: !!siteId,
      onSuccess: (data: KustomIntegrationSettingsType) => {
        if (data) {
          setFormData({
            site_id: data.site_id?.toString() || '',
            payouts_read_latest_poll_time: data.payouts_read_latest_poll_time || '',
          });
        }
      },
    }
  );

  // Save mutation
  const saveMutation = useMutation(
    (data: any) => {
      if (settings?.id) {
        return apiService.updateKustomIntegrationSettings(siteId!, data);
      } else {
        return apiService.createKustomIntegrationSettings(siteId!, data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['kustomIntegrationSettings', siteId]);
        showAlert('saveSuccess');
      },
      onError: (error) => {
        console.error('Save failed:', error);
        showAlert('error');
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    () => apiService.deleteKustomIntegrationSettings(siteId!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['kustomIntegrationSettings', siteId]);
        setFormData({
          site_id: '',
          payouts_read_latest_poll_time: '',
        });
        showAlert('deleteSuccess');
      },
      onError: (error) => {
        console.error('Delete failed:', error);
        showAlert('error');
      },
    }
  );

  const showAlert = (type: 'saveSuccess' | 'deleteSuccess' | 'error') => {
    setAlerts(prev => ({
      ...prev,
      [type]: true,
    }));

    setTimeout(() => {
      setAlerts(prev => ({
        ...prev,
        [type]: false,
      }));
    }, 3000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      site_id: parseInt(formData.site_id),
    };

    saveMutation.mutate(submitData);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete these integration settings?')) {
      deleteMutation.mutate();
    }
  };

  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toISOString().slice(0, 16); // Format: YYYY-MM-DDTHH:mm
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="section-title">Kustom Integration Settings</h1>
      </div>

      {/* Alerts */}
      {alerts.saveSuccess && (
        <div className="alert alert-success flex items-center space-x-2">
          <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4" />
          <span>Kustom Integration Settings saved</span>
        </div>
      )}

      {alerts.deleteSuccess && (
        <div className="alert alert-success flex items-center space-x-2">
          <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4" />
          <span>Kustom Integration Settings deleted</span>
        </div>
      )}

      {alerts.error && (
        <div className="alert alert-danger flex items-center space-x-2">
          <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4" />
          <span>Something went wrong</span>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit} className="form-section">
          <div className="form-row">
            <div className="form-column">
              <div>
                <label className="form-label form-label-required">Site:</label>
                <select
                  value={formData.site_id}
                  onChange={(e) => handleInputChange('site_id', e.target.value)}
                  className="form-input"
                  required
                >
                  <option value="">Select Site</option>
                  {sites.map((site: Site) => (
                    <option key={site.id} value={site.id}>
                      {site.url}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label">Latest Payout Read Time:</label>
                <input
                  type="datetime-local"
                  value={formatDateTime(formData.payouts_read_latest_poll_time)}
                  onChange={(e) => {
                    const isoString = e.target.value ? new Date(e.target.value).toISOString() : '';
                    handleInputChange('payouts_read_latest_poll_time', isoString);
                  }}
                  className="form-input"
                  placeholder="Select date and time"
                />
                <p className="text-sm text-gray-600 mt-1">
                  Format: YYYY-MM-DD HH:mm:ss
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6 space-x-3">
            {settings?.id && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="btn btn-danger flex items-center space-x-2"
              >
                {deleteMutation.isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            )}

            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
            >
              {saveMutation.isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} className="w-4 h-4" />
                  <span>Save</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KustomIntegration;
